import React, { useEffect, useRef } from 'react';

export function scheduleIdTokenRefresh(idToken, email, onRefresh) {
  const { exp } = JSON.parse(atob(idToken.split('.')[1]));   // seconds
  const millisUntilExpiry = exp * 1000 - Date.now();
  const early = 60_000;                                      // refresh 1 min early
  const delay = Math.max(millisUntilExpiry - early, 5_000);  // never <5 s

   return setTimeout(() => {
     /* re-initialise for a silent refresh of *this* account only */
     window.google.accounts.id.initialize({
       client_id : process.env.REACT_APP_GOOGLE_CLIENT_ID,
       login_hint: email,               // ← pre-select same user
       auto_select: true,
       callback  : ({ credential }) => {
         if (credential) onRefresh(credential);
         else console.warn('Silent re-sign-in failed; user interaction needed');
       },
     });
     window.google.accounts.id.prompt();   // runs silently if session is valid
   }, delay);
}

/**
 * Renders the GIS button and calls `onSuccess(idToken, profile)`
 * when the user signs in. This is Google Identity Services sign-in: it returns an
 * ID token only (name, email); no Gmail/Drive/Calendar scopes are requested.
 */
export default function GoogleLogin({ onSuccess }) {
  const btnRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // Load Google Identity Services once. public/index.html already includes it (other
    // code relies on window.google); a second copy makes two library instances render
    // into the same button container, which showed two sign-in buttons.
    let script = document.querySelector('script[src^="https://accounts.google.com/gsi/client"]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client?hl=en';
      script.async = true;
      script.defer = true;
      script.dataset.googleGsi = 'true';
      document.body.appendChild(script);
    }

    const mount = () => {
      if (cancelled || !window.google?.accounts?.id || !btnRef.current) return;
      //window.google.accounts.id.disableAutoSelect();
      
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        // Optional Google Workspace domain hint; server-side ALLOWED_DOMAINS is the real gate
        ...(process.env.REACT_APP_HOSTED_DOMAIN ? { hosted_domain: process.env.REACT_APP_HOSTED_DOMAIN } : {}),
        auto_select : false,  // Don't auto-select, let user explicitly click
        callback: ({ credential: idToken }) => {
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          onSuccess(idToken, {
            name: payload.name,
            email: payload.email,
            picture: payload.picture,
          });
        },
        ux_mode: 'popup',
      });

      // Don't auto-prompt - user must click the button

      window.google.accounts.id.renderButton(btnRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        locale: 'en',
      });
    };

    if (window.google?.accounts?.id) {
      mount();
    } else {
      script.addEventListener('load', mount);
    }

    return () => {
      cancelled = true;
      script.removeEventListener('load', mount);
    };
  }, [onSuccess]);

  // Everyone sees what signing in means (what Hermes PM gets and doesn't). The agent
  // wording shows only when opened from an agent's connect link (/authorize?user_code=...).
  const connectingAgent = window.location.pathname === '/authorize';

  return (
    <section className="centered" style={L.section}>
      <div style={L.info}>
        <h2 style={L.h2}>{connectingAgent ? 'Connect your agent to Hermes PM' : 'Sign in to Hermes PM'}</h2>
        <p style={L.lead}>
          {connectingAgent
            ? <>Your AI agent asked to connect to Hermes PM, the company's shared to-do list. You can ask
                your agent things like &lsquo;What&rsquo;s on my plate?&rsquo; or &lsquo;Put this on Tommy&rsquo;s
                list for Friday.&rsquo;</>
            : <>Hermes PM is the company's shared to-do list. You're signing in to Hermes PM with
                your work Google account.</>}
        </p>
      </div>
      <div ref={btnRef} id="g-btn" style={L.btn}></div>
      <div style={L.info}>
        <div style={L.box}>
          <div style={L.row}>
            {connectingAgent
              ? 'Your name and email address let your agent find your to-dos. No access to your email, files, calendar, or anything else in your Google account.'
              : 'Your name and email address let Hermes PM find your to-dos. No access to your email, files, calendar, or anything else in your Google account.'}
          </div>
        </div>
      </div>
      {connectingAgent && (
        <p style={L.foot}>Didn't ask your agent to connect? Close this page. Nothing happens unless you continue.</p>
      )}
    </section>
  );
}

const L = {
  section: { height: 'auto', minHeight: '100vh', boxSizing: 'border-box' },
  info: { width: '100%', maxWidth: 460, textAlign: 'left' },
  h2: { margin: '0 0 10px', textAlign: 'center' },
  lead: { fontSize: 15, lineHeight: 1.55, margin: '0 0 16px', color: 'var(--text-secondary)' },
  box: { border: '1px solid var(--border-color, rgba(127,127,127,.3))', borderRadius: 10, padding: '12px 14px', margin: '0 0 12px' },
  row: { display: 'flex', gap: 10, fontSize: 14, lineHeight: 1.5, margin: '4px 0' },
  yes: { color: '#2e9e5b', fontWeight: 700, width: 14, flexShrink: 0 },
  no: { color: '#d0503a', fontWeight: 700, width: 14, flexShrink: 0 },
  btn: { margin: '0 0 16px' },
  foot: { fontSize: 12, color: 'var(--text-secondary)', maxWidth: 460, textAlign: 'center', margin: 0 },
};
